import { useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";

import { useClient } from "../../../hooks/useClient";
import { useClientInvoices } from "../../../hooks/useClientInvoices";

import ClientFormModal from "../ClientFormModal";
import ClientDetailHeader from "./ClientDetailHeader";
import ClientDetailInvoicesList from "./ClientDetailInvoices";
import ClientDetailOverview from "./ClientDetailOverview";

import { getClientDisplayTitle } from "../../../utils/billing";

export default function ClientDetail() {
  const navigate = useNavigate();

  const { clientId } = useParams<{ clientId: string }>();

  const { client, loadingClient, fetchClientError, refetchClient } =
    useClient(clientId);

  const {
    invoices,
    loadingClientInvoices,
    fetchClientInvoicesError,
    refetchClientInvoices,
  } = useClientInvoices(clientId);

  // Client Edit Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleOpenEditModal = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleClientSaved = useCallback(() => {
    refetchClient();
    refetchClientInvoices();
  }, [refetchClient, refetchClientInvoices]);

  // Client Actions
  const handleArchiveClient = useCallback(() => {
    message.info(
      "Archive client is not available yet. This feature will be implemented soon.",
    );
  }, []);

  const handleDeleteClient = useCallback(() => {
    message.info(
      "Delete client is not available yet. This feature will be implemented soon.",
    );
  }, []);

  const handleCreateInvoice = useCallback(() => {
    message.info(
      "Create invoice is not available yet. This feature will be implemented soon.",
    );
  }, []);

  // Client Display Name
  const clientDisplayName = useMemo(() => {
    if (!client) return null;
    const { primary, secondary } = getClientDisplayTitle(client);
    return secondary && primary !== secondary
      ? `${primary} (${secondary})`
      : primary;
  }, [client]);

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {/* Header Bar */}
      <ClientDetailHeader
        displayName={clientDisplayName}
        loading={loadingClient}
        error={!!fetchClientError}
        actions={{
          onBack: () => navigate("/billing/clients"),
          onEdit: handleOpenEditModal,
          onArchiveClient: handleArchiveClient,
          onDeleteClient: handleDeleteClient,
        }}
      />

      {/* Client Overview */}
      <ClientDetailOverview
        client={client}
        loading={loadingClient}
        error={!!fetchClientError}
      />

      {/* Invoices List */}
      {client && (
        <ClientDetailInvoicesList
          invoices={invoices}
          loading={loadingClientInvoices}
          error={!!fetchClientInvoicesError}
          onRetry={refetchClientInvoices}
          onCreateInvoice={handleCreateInvoice}
          clientDisplayName={clientDisplayName}
        />
      )}

      {/* Client Edit Modal */}
      {client && (
        <ClientFormModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          editingClient={client}
          onClientSaved={handleClientSaved}
        />
      )}
    </div>
  );
}
